"use client";
import { SVariantDto } from "@/__generated__/sdk";
import { useCart, useCartHasProduct, useCartItem } from "@/redux/features/cart/hooks";
import { Button, CircularProgress, Icon, IconButton, Stack, Typography } from "@mui/material";
import { Add, Remove, ShoppingCart } from "@mui/icons-material";
import React, { useCallback, useEffect } from "react";
import { useAppDispatch } from "@/redux/hooks";
import { addOrUpdateItemInCart } from "@/redux/features/cart/cartSlice";
import { useMutation } from "@tanstack/react-query";
import { LoadingButton } from "@mui/lab";
import getError from "@/lib/getError";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";
import Link from "next/link";
import { useBreakPoints } from "@/lib/useBreakPoints";
import ShoppingBagIcon from "@/app/_components/Icons/ShoppingBagIcon";
import TrashIcon from "@/app/_components/Icons/TrashIcon";
import AddIcon from "@/app/_components/Icons/AddIcon";
import MinusIcon from "@/app/_components/Icons/MinusIcon";
import { sendGAEvent } from "@next/third-parties/google";
import { useBottomBar } from "@/app/(main)/_components/BottomBar/BottomBarProvider";
import VariantPrice from "@/app/(main)/products/[...slugs]/components/VariantPrice";
import VariantStockAlert from "@/app/(main)/products/[...slugs]/components/VariantStockAlert";
type Props = {
  variant: SVariantDto;
  productId: string;
  dontShowCartButton?: boolean;
};

export default function ProductVariantAddToCartSection({ variant, productId, dontShowCartButton }: Props) {
  const { mdAndDown } = useBreakPoints();
  const { cart, loading, itemsCount, error } = useCart();
  const maxBasketSize = variant.offer ? variant.offer.maxBasketCount : variant.maxBasketCount;

  const cartHasThisProduct = useCartHasProduct(productId);
  const cartItem = useCartItem(variant.id);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    if (error) {
      enqueueSnackbar(getError(error), { variant: "error" });
    }
  }, [error]);

  const dispatch = useAppDispatch();
  const { isPending, mutateAsync: addToCard } = useMutation({
    mutationFn: (quantity: number) => {
      return dispatch(
        addOrUpdateItemInCart({
          createCartInput: {
            quantity,
            variantId: variant.id,
            productId: productId,
          },
        }),
      ).unwrap();
    },
    onSuccess(data, quantity) {
      const item = data.cartItems.find((a) => a.variantId === variant.id && a.productId === productId);
      if (item) {
        // ارسال رویداد "cart_view" به Google Analytics
        sendGAEvent("event", "add_to_cart", {
          currency: "USD",
          value: item.totalPrice || null,
          items: [
            {
              item_id: item.product.code,
              item_name: item.product.title,
              price: item.totalPrice,
              quantity: item.quantity,
            },
          ],
        });
      }
    },
  });

  const handleAddToCartClick = (quantity: number = 1) => {
    if (quantity > maxBasketSize) {
      enqueueSnackbar("موجودی کافی نیست", { variant: "warning" });
      return;
    }
    addToCard(quantity);
  };
  const disableAdd = Boolean(cartItem?.quantity === maxBasketSize);

  return (
    <>
      <Stack sx={{ mt: `4px !important` }} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
        {cartItem ? (
          <Stack
            width={"100%"}
            useFlexGap
            flexWrap={"wrap-reverse"}
            spacing={1}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            {!dontShowCartButton && (
              <Button
                size={mdAndDown ? "medium" : "large"}
                sx={{ whiteSpace: "nowrap", borderRadius: 500, height: 44, backgroundColor: "grey.800" }}
                disableElevation={true}
                variant={"contained"}
                startIcon={<ShoppingBagIcon />}
                component={Link}
                href="/cart"
              >
                مشاهده سبد خرید
              </Button>
            )}
            <Box
              display="flex"
              sx={{ borderRadius: 50, backgroundColor: "grey.50", p: 0.5, ml: "auto" }}
              justifySelf={"center"}
              alignItems="center"
              gap={1}
            >
              <Button
                variant="contained"
                disableElevation
                sx={{ borderRadius: 100, minWidth: "auto", p: 1 }}
                onClick={() => {
                  handleAddToCartClick(cartItem.quantity + 1);
                }}
                disabled={disableAdd || isPending}
              >
                <Icon fontSize="small">
                  <AddIcon />
                </Icon>
              </Button>
              {isPending ? (
                <Box sx={{ minWidth: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress size={15} variant={"indeterminate"} />
                </Box>
              ) : (
                <Box sx={{ minWidth: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography variant="body1">{cartItem.quantity}</Typography>
                </Box>
              )}
              <Button
                variant="contained"
                disableElevation
                sx={{ borderRadius: 100, minWidth: "auto", p: 1 }}
                disabled={isPending}
                onClick={() => {
                  handleAddToCartClick(cartItem.quantity - 1);
                }}
              >
                {cartItem.quantity === 1 ? (
                  <Icon fontSize="small">
                    <TrashIcon />
                  </Icon>
                ) : (
                  <Icon fontSize="small">
                    <MinusIcon />
                  </Icon>
                )}
              </Button>
            </Box>
          </Stack>
        ) : (
          <LoadingButton
            loading={isPending}
            size={mdAndDown ? "medium" : "large"}
            variant={"contained"}
            disableElevation={true}
            fullWidth={true}
            sx={{ borderRadius: 500, height: 44, backgroundColor: "grey.800", whiteSpace: "nowrap" }}
            startIcon={<ShoppingBagIcon />}
            onClick={() => {
              handleAddToCartClick(1);
            }}
          >
            افزودن به سبد خرید
          </LoadingButton>
        )}
      </Stack>
    </>
  );
}
